/**
 * AlgoBuilderX Strategy Wizard
 *
 * No-code strategy builder with 4 steps:
 * 1. Indicators - Select and configure indicators
 * 2. Entry/Exit Rules - Define entry and exit logic
 * 3. Position Sizing - Lot sizer and risk manager
 * 4. Review - Summary and create
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { getComponents, createStrategy, validateStrategy } from '../../api/algoBuilderXApi';
import { getActiveSymbols } from '../../api/symbolApi';
import { mapComponents, groupComponentsByCategory, buildStrategyConfig } from '../../utils/algoBuilderXMapper';

import IndicatorStep from './steps/IndicatorStep';
import EntryExitStep from './steps/EntryExitStep';
import PositionSizingStep from './steps/PositionSizingStep';
import ReviewStep from './steps/ReviewStep';

const STEPS = ['Indicators', 'Entry & Exit Rules', 'Position Sizing', 'Review & Create'];

const AlgoBuilderXWizard = ({ open, onClose, onSave, token, accountNumber }) => {
  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Components from API
  const [components, setComponents] = useState({
    indicator: [],
    entryrule: [],
    exitrule: [],
    lotsizer: [],
    riskmanager: []
  });

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isEnabled: true,
    selectedSymbols: [],
    indicators: [],
    entryRule: null,
    exitRule: null,
    lotSizer: null,
    riskManager: null,
    enableGrid: false
  });

  // Validation errors per step
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch components on mount
  useEffect(() => {
    if (open && token) {
      fetchComponents();
    }
  }, [open, token]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      setError(null);

      const rawComponents = await getComponents(token);
      const mapped = mapComponents(rawComponents);
      const grouped = groupComponentsByCategory(mapped);

      setComponents(grouped);
      console.log('[AlgoBuilderX] Loaded components:', grouped);
    } catch (err) {
      console.error('[AlgoBuilderX] Failed to load components:', err);
      setError('Failed to load components. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update form data
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  // Validation per step
  const validateStep = (step) => {
    const errors = {};

    switch (step) {
      case 0: // Symbols + Indicators
        if (!formData.selectedSymbols || formData.selectedSymbols.length === 0) {
          errors.selectedSymbols = 'Select at least one symbol';
        }
        if (!formData.indicators || formData.indicators.length === 0) {
          errors.indicators = 'Select at least one indicator';
        }
        break;

      case 1: // Entry/Exit
        if (!formData.entryRule) {
          errors.entryRule = 'Select an entry rule';
        }
        if (!formData.exitRule) {
          errors.exitRule = 'Select an exit rule';
        }
        break;

      case 2: // Position Sizing
        if (!formData.lotSizer) {
          errors.lotSizer = 'Select a lot sizer';
        }
        break;

      case 3: // Review
        if (!formData.name || formData.name.trim().length < 3) {
          errors.name = 'Strategy name required (min 3 characters)';
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigation
  const handleNext = () => {
    if (!validateStep(activeStep)) {
      toast.error('Please fill in all required fields');
      return;
    }
    setValidationErrors({});
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setValidationErrors({});
    setActiveStep(prev => prev - 1);
  };

  // Submit strategy
  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Build strategy config
      const strategyConfig = buildStrategyConfig(formData, accountNumber);
      console.log('[AlgoBuilderX] Creating strategy:', strategyConfig);

      // Validate first
      const validation = await validateStrategy(token, strategyConfig);
      if (validation && validation.isValid === false) {
        setError(validation.errors?.join(', ') || 'Validation failed');
        return;
      }

      // Create strategy
      const result = await createStrategy(token, strategyConfig);

      toast.success(`Strategy "${formData.name}" created successfully!`);

      if (onSave) {
        onSave(result);
      }

      handleClose();
    } catch (err) {
      console.error('[AlgoBuilderX] Create failed:', err);
      setError(err.message || 'Failed to create strategy');
      toast.error(err.message || 'Failed to create strategy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      name: '',
      description: '',
      isEnabled: true,
      selectedSymbols: [],
      indicators: [],
      entryRule: null,
      exitRule: null,
      lotSizer: null,
      riskManager: null,
      enableGrid: false
    });
    setValidationErrors({});
    setError(null);
    onClose();
  };

  // Render step content
  const renderStepContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <IndicatorStep
            components={components.indicator}
            formData={formData}
            updateFormData={updateFormData}
            errors={validationErrors}
          />
        );

      case 1:
        return (
          <EntryExitStep
            entryComponents={components.entryrule}
            exitComponents={components.exitrule}
            formData={formData}
            updateFormData={updateFormData}
            errors={validationErrors}
          />
        );

      case 2:
        return (
          <PositionSizingStep
            lotSizerComponents={components.lotsizer}
            riskManagerComponents={components.riskmanager}
            formData={formData}
            updateFormData={updateFormData}
            errors={validationErrors}
          />
        );

      case 3:
        return (
          <ReviewStep
            formData={formData}
            updateFormData={updateFormData}
            errors={validationErrors}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a2e',
          color: '#fff',
          borderRadius: '12px',
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          pb: 2
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Strategy Builder
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Create a new trading strategy with AlgoBuilderX
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    color: index <= activeStep ? '#fff' : 'rgba(255,255,255,0.5)'
                  },
                  '& .MuiStepIcon-root': {
                    color: index < activeStep ? '#27B56D' : index === activeStep ? '#2563eb' : 'rgba(255,255,255,0.3)'
                  },
                  '& .MuiStepIcon-root.Mui-completed': {
                    color: '#27B56D'
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent()}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid rgba(255,255,255,0.1)',
          px: 3,
          py: 2,
          justifyContent: 'space-between'
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || submitting}
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          Back
        </Button>

        <Box>
          {activeStep === STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{
                backgroundColor: '#27B56D',
                '&:hover': { backgroundColor: '#22a861' }
              }}
            >
              {submitting ? 'Creating...' : 'Create Strategy'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: '#2563eb',
                '&:hover': { backgroundColor: '#1d4ed8' }
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AlgoBuilderXWizard;
